import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut, LayoutDashboard, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import BrandLogo from '@/components/portal/BrandLogo';

// WP2.1B: presentation migrated to EDS v1.0 tokens (forest chrome, emerald primary).
// All navigation, auth, active-state, dashboard-routing, and register deep-link
// logic is preserved unchanged from the frozen baseline.

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, role, signOut } = useAuth();

  const links = [
    { to: '/', label: 'Home' },
    { to: '/services', label: 'Services' },
    { to: '/features', label: 'Features' },
    { to: '/pricing', label: 'Access Levels' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  const dashboardPath =
    role === 'investor' ? '/investor/dashboard' : role === 'gis-analyst' ? '/gis/dashboard' : null;

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-eds-forest/95 backdrop-blur border-b border-eds-forest-2 font-eds-body">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" aria-label="GIISDEC home">
            <BrandLogo variant="light" subtitle="Geological Information & Investment Platform" />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-2 lg:px-3 py-2 rounded-eds-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-eds-mint/60 ${
                  isActive(l.to)
                    ? 'text-eds-mint bg-eds-forest-2'
                    : 'text-white/80 hover:text-white hover:bg-eds-forest-2'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                {/* md (768–1023): compact padding, decorative icons hidden, so Sign Out stays in view. */}
                {dashboardPath && (
                  <Link
                    to={dashboardPath}
                    className="inline-flex items-center gap-2 px-3 lg:px-4 py-2 text-sm font-medium text-white/90 border border-white/20 rounded-eds-md hover:bg-eds-forest-2"
                  >
                    <LayoutDashboard className="hidden lg:block w-4 h-4" /> Dashboard
                  </Link>
                )}
                <button
                  onClick={handleSignOut}
                  className="inline-flex items-center gap-2 px-3 lg:px-4 py-2 text-sm font-medium text-white bg-eds-surface/10 hover:bg-eds-surface/20 rounded-eds-md"
                >
                  <LogOut className="hidden lg:block w-4 h-4" /> Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/investor-login"
                  className="px-4 py-2 text-sm font-medium text-white/90 border border-white/20 rounded-eds-md hover:bg-eds-forest-2 transition"
                >
                  Sign In
                </Link>
                <Link
                  to="/investor-login#register"
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-eds-primary-fg bg-eds-primary rounded-eds-pill hover:bg-eds-emerald-2 transition"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
              </>
            )}
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="md:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {open && (
          <div className="md:hidden pb-4 space-y-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={`block px-3 py-2 rounded-eds-sm text-sm font-medium ${
                  isActive(l.to) ? 'text-eds-mint bg-eds-forest-2' : 'text-white/80 hover:bg-eds-forest-2'
                }`}
              >
                {l.label}
              </Link>
            ))}
            {user ? (
              <>
                {dashboardPath && (
                  <Link
                    to={dashboardPath}
                    onClick={() => setOpen(false)}
                    className="block px-3 py-2 rounded-eds-md text-sm font-medium text-white/90 border border-white/20 mt-2"
                  >
                    Dashboard
                  </Link>
                )}
                <button
                  onClick={() => { setOpen(false); handleSignOut(); }}
                  className="block w-full text-left px-3 py-2 rounded-eds-md text-sm font-medium text-white bg-eds-surface/10"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/investor-login"
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2 rounded-eds-md text-sm font-medium text-white/90 border border-white/20 mt-2"
                >
                  Sign In
                </Link>
                <Link
                  to="/investor-login#register"
                  onClick={() => setOpen(false)}
                  className="block px-3 py-2 rounded-eds-md text-sm font-semibold text-eds-primary-fg bg-eds-primary text-center"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
